/**
 * Shared persona types — single source of truth for the API response shape.
 * Used by LibrarySidebar, PersonaPicker, EditWithAIView, and the Composer.
 *
 * Mirrors `skills.ts` by design — personas and skills share nearly identical
 * shapes and the generic `LibraryRow` component consumes both.
 *
 * Per project rule (Schemas Are the Source of Truth): the Zod schema is
 * canonical and the TS type is `z.infer<typeof PersonaBaseSchema>`.
 * Wire data is observational-validated through `validateResponse` in the
 * persona editor so a server shape change surfaces as a logged warning.
 */

import { z } from 'zod';
import { LibraryVisibilitySchema, PersonaEntrySchema } from '@shared/schemas/library-entity';

/** Base persona fields returned by all API endpoints. */
export const PersonaBaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  icon: z.string().nullable(),
  content: z.string(),
  visibility: z.enum(['personal', 'team']),
  createdById: z.number(),
  forkedFromId: z.string().nullable(),
  enabled: z.boolean(),
  usageCount: z.number(),
  /** Library-applicability tags (U2) — which product surfaces this persona
   *  appears on. Mirror of `KNOWN_SURFACES_CLIENT`. Defaults to
   *  `['edit-with-ai']` server-side; empty array = hidden everywhere. */
  availableIn: z.array(z.string()),
});

export type PersonaBase = z.infer<typeof PersonaBaseSchema>;

/**
 * Persona with computed fields from the list endpoint (GET /api/v4/personas):
 * the server's list-entry schema, with `visibility` closed to the two members
 * the client branches on and the dates read as the ISO strings on the wire.
 */
export const PersonaListItemSchema = PersonaEntrySchema.extend({
  visibility: LibraryVisibilitySchema,
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type PersonaListItem = z.output<typeof PersonaListItemSchema>;
